import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import {
  IFuelConsumptionWindowResponse,
  IFuelMachineDetailResponse,
  IFuelMachineLeagueResponse,
} from 'types/api/fuelConsumptionApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IFuelConsumptionSlice } from 'types/fuelConsumptionSlice.interface'
import { baseUrl } from 'utils/axios'
import { formatParams, handleErrors } from 'utils/helpers'

const initialState: IFuelConsumptionSlice = {
  isLoading: false,
  machines: {
    data: [],
    totalCount: 0,
    totalPages: 0,
    isLoading: false,
  },
  machineDetail: null,
  machineDetailLoading: false,
  consumptionWindows: [],
  consumptionLoading: false,
}

export const getConsumptionMachines = createAsyncThunk(
  'fuelConsumption/getMachines',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<IFuelMachineLeagueResponse>> = await baseUrl.get(
        'fuel/machines',
        { params: formatParams(params) },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getConsumptionMachineDetail = createAsyncThunk(
  'fuelConsumption/getMachineDetail',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelMachineDetailResponse> = await baseUrl.get(`fuel/machines/${id}`)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getMachineConsumption = createAsyncThunk(
  'fuelConsumption/getMachineConsumption',
  async ({ id, params }: { id: number; params?: any }, thunkAPI) => {
    try {
      const resp: AxiosResponse<IFuelConsumptionWindowResponse[]> = await baseUrl.get(
        `fuel/machines/${id}/consumption`,
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const fuelConsumptionSlice = createSlice({
  name: 'fuelConsumption',
  initialState,
  reducers: {
    clearFuelConsumption: () => initialState,
    clearMachineDetail: (state) => {
      state.machineDetail = null
      state.consumptionWindows = []
    },
  },
  extraReducers: {
    [getConsumptionMachines.pending.type]: (state) => {
      state.machines.isLoading = true
    },
    [getConsumptionMachines.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<IFuelMachineLeagueResponse> },
    ) => {
      state.machines.isLoading = false
      state.machines.data = payload.data
      state.machines.totalCount = payload.totalCount
      state.machines.totalPages = payload.totalPages
    },
    [getConsumptionMachines.rejected.type]: (state, { payload }) => {
      state.machines.isLoading = false
      handleErrors(payload)
    },
    [getConsumptionMachineDetail.pending.type]: (state) => {
      state.machineDetailLoading = true
    },
    [getConsumptionMachineDetail.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelMachineDetailResponse },
    ) => {
      state.machineDetailLoading = false
      state.machineDetail = payload
    },
    [getConsumptionMachineDetail.rejected.type]: (state, { payload }) => {
      state.machineDetailLoading = false
      handleErrors(payload)
    },
    [getMachineConsumption.pending.type]: (state) => {
      state.consumptionLoading = true
    },
    [getMachineConsumption.fulfilled.type]: (
      state,
      { payload }: { payload: IFuelConsumptionWindowResponse[] },
    ) => {
      state.consumptionLoading = false
      state.consumptionWindows = payload
    },
    [getMachineConsumption.rejected.type]: (state, { payload }) => {
      state.consumptionLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearFuelConsumption, clearMachineDetail } = fuelConsumptionSlice.actions
export default fuelConsumptionSlice.reducer
